/**
 * DialogSystem — Manages dialog state, branching, attribute checks,
 * and rewards for the dialog/conversation system.
 *
 * Loads dialog data from dialogs.json. Tracks the current dialog,
 * current node, and provides methods to advance through the tree.
 *
 * @module DialogSystem
 */

import eventBus from '../core/EventBus.js';
import debugLog from '../debug/DebugLog.js';
import dialogsData from '../data/dialogs.json';

class DialogSystem {
  constructor() {
    /** @type {Map<string, object>} Dialog definitions keyed by id */
    this._dialogs = new Map();

    /** @type {object|null} Current active dialog definition */
    this._currentDialog = null;

    /** @type {object|null} Current node within the active dialog */
    this._currentNode = null;

    /** @type {string|null} Current node id */
    this._currentNodeId = null;

    /** @type {object|null} Context passed when dialog started */
    this._context = null;

    /** @type {object|null} Last check result { roll, modifier, total, dc, success } */
    this._lastCheckResult = null;

    /** @type {boolean} Whether the dialog has ended */
    this._ended = false;

    // Load dialog data
    this._loadDialogs();
  }

  /**
   * Load all dialog definitions from the imported JSON data.
   */
  _loadDialogs() {
    if (!Array.isArray(dialogsData)) {
      debugLog.warn('DialogSystem: dialogs.json is not an array');
      return;
    }

    for (const dialog of dialogsData) {
      if (dialog.id) {
        this._dialogs.set(dialog.id, structuredClone(dialog));
      }
    }

    debugLog.info(`DialogSystem: Loaded ${this._dialogs.size} dialogs`);
  }

  /**
   * Start a dialog by ID.
   * @param {string} dialogId
   * @param {object} [context] - { party, character, flags, ... }
   * @returns {boolean} true if dialog started successfully
   */
  startDialog(dialogId, context = {}) {
    const dialog = this._dialogs.get(dialogId);
    if (!dialog) {
      debugLog.warn(`DialogSystem: Dialog not found: ${dialogId}`);
      return false;
    }

    this._currentDialog = structuredClone(dialog);
    this._context = context;
    this._ended = false;
    this._lastCheckResult = null;

    // Navigate to start node
    const startNode = this._currentDialog.nodes[this._currentDialog.startNodeId];
    if (!startNode) {
      debugLog.error(`DialogSystem: Start node "${this._currentDialog.startNodeId}" not found in dialog "${dialogId}"`);
      return false;
    }

    this._currentNodeId = this._currentDialog.startNodeId;
    this._currentNode = startNode;

    debugLog.event(`DialogSystem: Started dialog "${dialogId}"`);
    eventBus.emit('dialog:start', {
      dialogId,
      title: this._currentDialog.title,
      node: this._currentNode,
    });

    return true;
  }

  /**
   * Get the current dialog definition.
   * @returns {object|null}
   */
  getCurrentDialog() {
    return this._currentDialog;
  }

  /**
   * Get the current node.
   * @returns {object|null}
   */
  getCurrentNode() {
    return this._currentNode;
  }

  /**
   * Get the last attribute check result.
   * @returns {object|null} { roll, modifier, total, dc, success, attribute }
   */
  getLastCheckResult() {
    return this._lastCheckResult;
  }

  /**
   * Get available options for the current node, filtered by requirements.
   * Returns enriched option objects with metadata about availability.
   *
   * @returns {Array<{text: string, index: number, available: boolean, hidden: boolean, revealed: boolean, meetsCheck: boolean|null, requirementText: string|null, check: object|null}>}
   */
  getAvailableOptions() {
    if (!this._currentNode || !this._currentNode.options) {
      return [];
    }

    const results = [];
    const options = this._currentNode.options;

    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      const result = {
        text: opt.text,
        index: i,
        available: true,
        hidden: !!opt.hidden,
        revealed: false,
        meetsCheck: null,
        requirementText: null,
        check: opt.check || null,
      };

      // Check requirements
      if (opt.requires) {
        const reqCheck = this._checkRequirements(opt.requires);
        result.available = reqCheck.met;
        result.requirementText = reqCheck.text;

        if (opt.hidden) {
          // Hidden options only show when requirements are met
          result.revealed = reqCheck.met;
        }
      } else if (opt.hidden) {
        // Hidden option with no requirements - always hidden (unlikely but handle)
        result.revealed = false;
      }

      // Check attribute requirements for color coding
      if (opt.check) {
        const character = this._getCheckCharacter();
        if (character) {
          const attrValue = character.attributes[opt.check.attribute] || 10;
          const modifier = Math.floor((attrValue - 10) / 2);
          result.meetsCheck = (attrValue >= opt.check.dc) || (modifier + 10 >= opt.check.dc);
          // More precisely: can they reasonably pass? Show green if attribute >= dc
          result.meetsCheck = attrValue >= opt.check.dc;
        }
      }

      results.push(result);
    }

    return results;
  }

  /**
   * Select an option by index. Handles attribute checks and advances to next node.
   * @param {number} index - Option index
   * @returns {boolean} true if option was selected
   */
  selectOption(index) {
    if (!this._currentNode || !this._currentNode.options) return false;

    const option = this._currentNode.options[index];
    if (!option) return false;

    // Check if option has requirements that aren't met
    if (option.requires) {
      const reqCheck = this._checkRequirements(option.requires);
      if (!reqCheck.met) {
        debugLog.warn(`DialogSystem: Cannot select option ${index}, requirements not met`);
        return false;
      }
    }

    this._lastCheckResult = null;

    eventBus.emit('dialog:option-selected', {
      index,
      text: option.text,
    });

    // Handle attribute check
    if (option.check) {
      const character = this._getCheckCharacter();
      const checkResult = this.rollAttributeCheck(
        option.check.attribute,
        option.check.dc,
        character
      );

      this._lastCheckResult = checkResult;

      if (checkResult.success) {
        eventBus.emit('dialog:check-pass', checkResult);
        this._navigateToNode(option.nextNodeId);
      } else {
        eventBus.emit('dialog:check-fail', checkResult);
        this._navigateToNode(option.failNodeId || option.nextNodeId);
      }
    } else {
      // No check, go directly to next node
      this._navigateToNode(option.nextNodeId);
    }

    return true;
  }

  /**
   * Roll an attribute check: d20 + modifier vs DC.
   * @param {string} attribute - Attribute key (str, agi, int, etc.)
   * @param {number} dc - Difficulty class
   * @param {object} [character] - Character with attributes
   * @returns {{ roll: number, modifier: number, total: number, dc: number, success: boolean, attribute: string }}
   */
  rollAttributeCheck(attribute, dc, character) {
    const attrValue = character?.attributes?.[attribute] ?? 10;
    const modifier = Math.floor((attrValue - 10) / 2);
    const roll = Math.floor(Math.random() * 20) + 1;
    const total = roll + modifier;
    const success = total >= dc;

    const result = { roll, modifier, total, dc, success, attribute };

    debugLog.info(
      `DialogSystem: Check ${attribute.toUpperCase()} DC ${dc}: ` +
      `Roll ${roll} + ${modifier} = ${total} -> ${success ? 'PASS' : 'FAIL'}`
    );

    return result;
  }

  /**
   * Check if the dialog has ended (current node has no options).
   * @returns {boolean}
   */
  isComplete() {
    return this._ended;
  }

  /**
   * End the current dialog.
   */
  endDialog() {
    if (!this._currentDialog) return;

    const dialogId = this._currentDialog.id;
    this._ended = true;

    eventBus.emit('dialog:end', { dialogId });
    debugLog.event(`DialogSystem: Dialog "${dialogId}" ended`);

    this._currentDialog = null;
    this._currentNode = null;
    this._currentNodeId = null;
    this._context = null;
    this._lastCheckResult = null;
  }

  /**
   * Get a dialog definition by ID (without starting it).
   * @param {string} dialogId
   * @returns {object|null}
   */
  getDialog(dialogId) {
    return this._dialogs.get(dialogId) || null;
  }

  /**
   * Get all dialog IDs.
   * @returns {string[]}
   */
  getAllDialogIds() {
    return [...this._dialogs.keys()];
  }

  /**
   * Register a new dialog definition at runtime.
   * @param {object} dialog
   */
  registerDialog(dialog) {
    if (!dialog || !dialog.id) return;
    this._dialogs.set(dialog.id, structuredClone(dialog));
    debugLog.info(`DialogSystem: Registered dialog "${dialog.id}"`);
  }

  // ── Private Helpers ────────────────────────────────

  /**
   * Navigate to a specific node by ID. Apply rewards if present.
   * @param {string} nodeId
   */
  _navigateToNode(nodeId) {
    if (!nodeId || !this._currentDialog) {
      // End of dialog
      this._applyRewards(this._currentNode);
      this.endDialog();
      return;
    }

    const node = this._currentDialog.nodes[nodeId];
    if (!node) {
      debugLog.error(`DialogSystem: Node "${nodeId}" not found`);
      this._applyRewards(this._currentNode);
      this.endDialog();
      return;
    }

    // Apply rewards from current node before leaving
    // (rewards are applied when entering a node, not leaving)
    // Actually, apply rewards from the NEW node when we arrive
    this._currentNodeId = nodeId;
    this._currentNode = node;

    // Apply rewards for this node
    this._applyRewards(node);

    // Check if this is a terminal node (no options)
    if (!node.options || node.options.length === 0) {
      this._ended = true;
      eventBus.emit('dialog:node-changed', { nodeId, node, isEnd: true });
      debugLog.event(`DialogSystem: Dialog "${this._currentDialog.id}" reached end node "${nodeId}"`);
    } else {
      eventBus.emit('dialog:node-changed', { nodeId, node, isEnd: false });
    }
  }

  /**
   * Apply rewards from a node.
   * @param {object} node
   */
  _applyRewards(node) {
    if (!node || !node.rewards) return;

    const rewards = node.rewards;

    // Gold reward
    if (rewards.gold && rewards.gold !== 0) {
      const party = this._context?.party;
      if (party) {
        party.gold = (party.gold || 0) + rewards.gold;
      }
      eventBus.emit('dialog:reward', { type: 'gold', amount: rewards.gold });
      debugLog.info(`DialogSystem: Reward gold ${rewards.gold > 0 ? '+' : ''}${rewards.gold}`);
    }

    // Flag rewards
    if (rewards.flags && Array.isArray(rewards.flags)) {
      const flags = this._context?.flags;
      if (flags) {
        for (const flag of rewards.flags) {
          if (flags instanceof Set) {
            flags.add(flag);
          } else if (typeof flags === 'object') {
            flags[flag] = true;
          }
        }
      }
      eventBus.emit('dialog:reward', { type: 'flags', flags: rewards.flags });
      debugLog.info(`DialogSystem: Reward flags [${rewards.flags.join(', ')}]`);
    }

    // Item rewards
    if (rewards.items && Array.isArray(rewards.items)) {
      eventBus.emit('dialog:reward', { type: 'items', items: rewards.items });
      debugLog.info(`DialogSystem: Reward items [${rewards.items.join(', ')}]`);
    }

    // Quest trigger
    if (rewards.quest) {
      eventBus.emit('dialog:reward', { type: 'quest', questId: rewards.quest });
      debugLog.info(`DialogSystem: Reward quest "${rewards.quest}"`);
    }
  }

  /**
   * Check if requirements are met for an option.
   * @param {object} requires - { race?, flag?, minGold?, item? }
   * @returns {{ met: boolean, text: string }}
   */
  _checkRequirements(requires) {
    const context = this._context || {};

    // Race check
    if (requires.race) {
      const character = this._getCheckCharacter();
      const raceId = character?.raceId;
      if (raceId !== requires.race) {
        const raceName = requires.race.charAt(0).toUpperCase() + requires.race.slice(1);
        return { met: false, text: `Requires: ${raceName}` };
      }
    }

    // Flag check
    if (requires.flag) {
      const flags = context.flags;
      let hasFlag = false;
      if (flags instanceof Set) {
        hasFlag = flags.has(requires.flag);
      } else if (typeof flags === 'object' && flags) {
        hasFlag = !!flags[requires.flag];
      }
      if (!hasFlag) {
        return { met: false, text: `Requires: Special knowledge` };
      }
    }

    // Gold check
    if (requires.minGold !== undefined) {
      const party = context.party;
      const gold = party?.gold || 0;
      if (gold < requires.minGold) {
        return { met: false, text: `Requires: ${requires.minGold} gold` };
      }
    }

    // Item check
    if (requires.item) {
      // For now, check flags for item possession
      const flags = context.flags;
      let hasItem = false;
      if (flags instanceof Set) {
        hasItem = flags.has(`has_${requires.item}`);
      } else if (typeof flags === 'object' && flags) {
        hasItem = !!flags[`has_${requires.item}`];
      }
      if (!hasItem) {
        return { met: false, text: `Requires: ${requires.item}` };
      }
    }

    return { met: true, text: null };
  }

  /**
   * Get the character to use for attribute checks.
   * Uses the party leader or the specific character from context.
   * @returns {object|null}
   */
  _getCheckCharacter() {
    if (this._context?.character) return this._context.character;
    if (this._context?.party?.getLeader) return this._context.party.getLeader();
    if (this._context?.party?.members?.[0]) return this._context.party.members[0];
    return null;
  }
}

// Singleton
const dialogSystem = new DialogSystem();
export default dialogSystem;
